import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError, inr, int, when } from '../../api';
import { Badge, Empty, Panel, useAsync } from '../../components';
import { DataTable } from '../../ui/DataTable';

interface IssueRow { id: number; rule_id: string; rule_name: string; domain: string; kind: string; system: string; entity_type: string; severity: string; title: string; affected_count: number; financial_exposure: string | null; exposure_basis: string | null; status: string; owner: string | null; due_on: string | null; last_seen_at: string; seen_count: number }
interface IssueDetail { issue: IssueRow & { detail: Record<string, unknown>; affected_sample: unknown[]; rule_description: string; first_seen_at: string }; corrections: unknown[] }

const SEV_LABEL: Record<string, string> = { S1: 'Critical — money, tax or entity', S2: 'Material — analytics and decisions', S3: 'Hygiene' };

export function Issues() {
  const { id } = useParams();
  const nav = useNavigate();
  const [sev, setSev] = React.useState<string>('');
  const [status, setStatus] = React.useState<string>('open');
  const list = useAsync(() => api<{ issues: IssueRow[] }>(`/api/v1/mdm/issues?limit=300${sev ? `&severity=${sev}` : ''}${status ? `&status=${status}` : ''}`), [sev, status]);
  return (
    <div className="page">
      <div className="page-head">
        <div><div className="xs">Data intelligence &amp; governance</div><h1>Findings &amp; exceptions</h1><p>Every record flagged by a rule, ranked by severity and the stock sitting on it. Open one for the exact reason and the sample records.</p></div>
      </div>
      <div className="toolbar">
        {['', 'S1', 'S2', 'S3'].map(s => <button key={s} className={`btn ${sev === s ? 'on' : ''}`} onClick={() => setSev(s)}>{s ? SEV_LABEL[s] : 'All severities'}</button>)}
        <span style={{ width: 16 }} />
        {['open', 'investigating', 'resolved', 'dismissed', ''].map(s => <button key={s} className={`btn ${status === s ? 'on' : ''}`} onClick={() => setStatus(s)}>{s || 'Any status'}</button>)}
      </div>
      <Panel title={`${list.data?.issues.length ?? '…'} findings`}>
        {list.data?.issues.length ? (
          <DataTable rows={list.data.issues.map(i => ({ ...i, key: String(i.id), exposure: i.financial_exposure != null ? Number(i.financial_exposure) : null }))} exportName="findings" title="Findings and exceptions" onRow={i => nav(`/settings/data-quality/findings/${i.id}`)} columns={[
            { key: 'severity', title: 'Severity', kind: 'node', get: i => <Badge kind={i.severity}>{i.severity}</Badge>, raw: i => i.severity, sortBy: i => i.severity },
            { key: 'title', title: 'Finding', kind: 'node', get: i => <>{i.title}<div className="muted small">{i.rule_name} · {i.system} · {i.entity_type}</div></>, raw: i => `${i.title} (${i.rule_name} · ${i.system} · ${i.entity_type})`, sortBy: i => i.title },
            { key: 'domain', title: 'Domain', kind: 'text' }, { key: 'affected_count', title: 'Records', kind: 'units' }, { key: 'exposure', title: 'Stock at MRP', kind: 'money' },
            { key: 'status', title: 'Status', kind: 'node', get: i => <Badge kind={i.status === 'open' ? 'plain' : i.status === 'resolved' ? 'ok' : 'info'}>{i.status}</Badge>, raw: i => i.status, sortBy: i => i.status },
            { key: 'last_seen_at', title: 'Last seen', kind: 'node', get: i => <span className="small num">{when(i.last_seen_at)}{i.seen_count > 1 ? <span className="muted"> · seen {i.seen_count}×</span> : null}</span>, raw: i => i.last_seen_at, sortBy: i => i.last_seen_at },
          ]} />
        ) : <Empty title="Nothing matches">{list.error ?? 'Change the filters, or run the profiler.'}</Empty>}
      </Panel>
      {id && <IssueDrawer id={Number(id)} onClose={() => nav('/settings/data-quality/findings')} onChanged={list.reload} />}
    </div>
  );
}

function IssueDrawer({ id, onClose, onChanged }: { id: number; onClose: () => void; onChanged: () => void }) {
  const d = useAsync(() => api<IssueDetail>(`/api/v1/mdm/issues/${id}`), [id]);
  const [reason, setReason] = React.useState('');
  const [err, setErr] = React.useState<string | null>(null);
  const i = d.data?.issue;
  const act = async (status: string) => {
    setErr(null);
    try { await api(`/api/v1/mdm/issues/${id}/status`, { method: 'POST', body: JSON.stringify({ status, reason }) }); d.reload(); onChanged(); setReason(''); }
    catch (e) { setErr(e instanceof ApiError ? (e.code === 'FORBIDDEN' ? 'Only a data steward or the owner can change a finding.' : e.message) : String(e)); }
  };
  return (
    <>
      <div className="scrim" onClick={onClose} />
      <aside className="drawer" role="dialog" aria-label="Finding detail">
        {!i ? <Empty title="Loading…" /> : (
          <>
            <div><Badge kind={i.severity}>{i.severity} · {SEV_LABEL[i.severity]}</Badge><h2 style={{ marginTop: 10 }}>{i.title}</h2><div className="muted small" style={{ marginTop: 4 }}>{i.rule_name} — {i.rule_description}</div></div>
            <dl className="kv">
              <dt>Rule</dt><dd className="num">{i.rule_id}</dd>
              <dt>Source</dt><dd>{i.system} · {i.entity_type}</dd>
              <dt>Records affected</dt><dd className="num">{int(i.affected_count)}</dd>
              <dt>Stock on these records</dt><dd className="num">{i.financial_exposure ? `${inr(i.financial_exposure)} — ${i.exposure_basis}` : 'not sized'}</dd>
              <dt>First seen</dt><dd className="num">{when(i.first_seen_at)}</dd>
              <dt>Last seen</dt><dd className="num">{when(i.last_seen_at)} · {i.seen_count} run{i.seen_count > 1 ? 's' : ''}</dd>
              <dt>Status</dt><dd><Badge kind="plain">{i.status}</Badge>{i.owner ? ` · ${i.owner}` : ''}{i.due_on ? ` · due ${i.due_on}` : ''}</dd>
            </dl>
            <div><h3>Why it was flagged</h3><pre className="code">{JSON.stringify(i.detail, null, 1)}</pre></div>
            <div><h3>Sample records</h3><pre className="code">{JSON.stringify(i.affected_sample, null, 1)}</pre></div>
            <div>
              <h3>Decision</h3>
              <p className="muted small">Every change needs a reason and is written to the append-only audit trail. Nothing here changes a source system.</p>
              <textarea rows={2} style={{ width: '100%' }} placeholder="Reason (required)" value={reason} onChange={e => setReason(e.target.value)} />
              <div className="toolbar" style={{ marginTop: 8 }}>
                <button className="btn primary" disabled={reason.length < 3} onClick={() => act('investigating')}>Investigate</button>
                <button className="btn" disabled={reason.length < 3} onClick={() => act('resolved')}>Mark resolved</button>
                <button className="btn danger" disabled={reason.length < 3} onClick={() => act('dismissed')}>Dismiss</button>
                <button className="btn" onClick={onClose} style={{ marginLeft: 'auto' }}>Close</button>
              </div>
              {err && <div className="callout" style={{ marginTop: 8 }}>{err}</div>}
            </div>
          </>
        )}
      </aside>
    </>
  );
}
